import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Loader2, AlertTriangle, Users, Brain, Network, Activity, Shield } from "lucide-react";
import { SorinHeader } from "@/components/SorinHeader";
import { motion } from "framer-motion";

interface WorkerProfile {
  id: string;
  display_name: string;
  full_name: string;
  position: string;
  team: string;
  current_risk_score: number;
  baseline_risk_score: number;
  total_xp: number;
  consecutive_safe_days: number;
  verified_incidents_count: number;
  network_centrality: number;
  network_role: string;
  clustering_coefficient: number;
  risky_neighbor_count: number;
  safety_influence_score: number;
  is_wide_bridge: boolean;
  psychometric_profile: any;
  contagion_risk_score: number;
  years_of_experience: number;
  credibility_score: number;
}

interface NetworkConnection {
  id: string;
  connection_type: string;
  connection_weight: number;
  other_worker_name: string;
}

const UserDetail = () => {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<WorkerProfile | null>(null);
  const [connections, setConnections] = useState<NetworkConnection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workerId) {
      loadWorkerData();
    }
  }, [workerId]);

  const loadWorkerData = async () => {
    try {
      // Load worker profile
      const { data: workerData, error } = await supabase
        .from("worker_profiles")
        .select("*")
        .eq("id", workerId)
        .single();

      if (error) throw error;
      setProfile(workerData);

      // Load network connections
      const { data: connectionsData } = await supabase
        .from("worker_network_connections")
        .select(`
          id,
          connection_type,
          connection_weight,
          worker_b
        `)
        .eq("worker_a", workerId);

      if (connectionsData && connectionsData.length > 0) {
        // Get names for connected workers
        const workerBIds = connectionsData.map(c => c.worker_b);
        const { data: workerNames } = await supabase
          .from("worker_profiles")
          .select("id, display_name")
          .in("id", workerBIds);

        const nameMap = new Map(workerNames?.map(w => [w.id, w.display_name]) || []);
        
        setConnections(connectionsData.map(c => ({
          id: c.id,
          connection_type: c.connection_type,
          connection_weight: c.connection_weight || 1,
          other_worker_name: nameMap.get(c.worker_b) || "Unknown"
        })));
      }
    } catch (error) {
      console.error("Error loading worker data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role: string | null) => {
    const roleConfig: Record<string, { color: string; label: string }> = {
      champion: { color: "bg-success text-success-foreground", label: "Champion" },
      bridge: { color: "bg-primary text-primary-foreground", label: "Bridge" },
      influencer: { color: "bg-secondary text-secondary-foreground", label: "Influencer" },
      learner: { color: "bg-warning text-warning-foreground", label: "Learner" },
      isolate: { color: "bg-muted text-muted-foreground", label: "Isolate" }
    };
    const config = roleConfig[role || "learner"] || roleConfig.learner;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getRiskColor = (score: number) => {
    if (score >= 70) return "text-destructive";
    if (score >= 40) return "text-warning";
    return "text-success";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Worker not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const psychProfile = profile.psychometric_profile as any;

  return (
    <div className="min-h-screen bg-background">
      <SorinHeader />
      
      <main className="container max-w-4xl mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: -10 }} 
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 mb-6"
        >
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="rounded-xl">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">{profile.display_name}</h1>
            <p className="text-sm text-muted-foreground">{profile.position || profile.team}</p>
          </div>
          {getRoleBadge(profile.network_role)}
        </motion.div>

        <Tabs defaultValue="risk" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="risk">Risk Score</TabsTrigger>
            <TabsTrigger value="network">Network</TabsTrigger>
            <TabsTrigger value="psychometric">Psychometric</TabsTrigger>
            <TabsTrigger value="connections">Connections</TabsTrigger>
          </TabsList>

          {/* Risk Score Breakdown */}
          <TabsContent value="risk">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className={`h-8 w-8 ${getRiskColor(profile.current_risk_score)}`} />
                <div>
                  <div className="text-3xl font-bold">{profile.current_risk_score}</div>
                  <div className="text-sm text-muted-foreground">Current Risk Score</div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Brain className="h-4 w-4" />
                    Baseline Risk (Psychometric)
                  </h4>
                  <div className="flex items-center gap-3">
                    <Progress value={profile.baseline_risk_score} className="flex-1" />
                    <span className="font-medium w-12 text-right">{profile.baseline_risk_score}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Based on UPPS-P, DOSPERT, and Delay Discounting scores
                  </p>
                </div>

                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Behavioral Factors
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">Verified Incidents</div>
                      <div className="text-xl font-bold text-destructive">{profile.verified_incidents_count}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">Safe Days Streak</div>
                      <div className="text-xl font-bold text-success">{profile.consecutive_safe_days}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">Credibility Score</div>
                      <div className="text-xl font-bold">{profile.credibility_score || 100}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">Years Experience</div>
                      <div className="text-xl font-bold">{profile.years_of_experience || 0}</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Network className="h-4 w-4" />
                    Network Factors
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">Risky Neighbors</div>
                      <div className="text-xl font-bold text-warning">{profile.risky_neighbor_count || 0}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">Contagion Risk</div>
                      <div className="text-xl font-bold">{profile.contagion_risk_score || 0}%</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Network Metrics */}
          <TabsContent value="network">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Network className="h-8 w-8 text-primary" />
                <div>
                  <div className="text-3xl font-bold">{profile.network_centrality || 0}</div>
                  <div className="text-sm text-muted-foreground">Network Connections</div>
                </div>
                {profile.is_wide_bridge && (
                  <Badge className="ml-auto">Wide Bridge</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <div className="text-2xl font-bold">{profile.network_role || "—"}</div>
                  <div className="text-xs text-muted-foreground">Network Role</div>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <div className="text-2xl font-bold">{profile.clustering_coefficient || 0}</div>
                  <div className="text-xs text-muted-foreground">Clustering Coef.</div>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <div className="text-2xl font-bold text-success">{profile.safety_influence_score || 0}</div>
                  <div className="text-xs text-muted-foreground">Safety Influence</div>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <div className="text-2xl font-bold text-warning">{profile.risky_neighbor_count || 0}</div>
                  <div className="text-xs text-muted-foreground">Risky Neighbors</div>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-lg border">
                <h5 className="font-medium mb-2">Role Description</h5>
                <p className="text-sm text-muted-foreground">
                  {profile.network_role === "champion" && "Safety Champion: High connections with low risk. Ideal for mentoring and spreading safe behaviors."}
                  {profile.network_role === "bridge" && "Wide Bridge: Connects multiple teams/clusters. Critical for spreading behaviors across groups."}
                  {profile.network_role === "influencer" && "Influencer: Good connections with moderate risk. Has potential to influence peers positively."}
                  {profile.network_role === "learner" && "Learner: Higher risk worker who needs guidance and intervention from champions."}
                  {profile.network_role === "isolate" && "Isolate: Few connections. May need to be integrated into learning circles."}
                  {!profile.network_role && "Role not yet determined. Run network metrics calculation."}
                </p>
              </div>
            </Card>
          </TabsContent>

          {/* Psychometric Profile */}
          <TabsContent value="psychometric">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Brain className="h-8 w-8 text-primary" />
                <div>
                  <div className="text-lg font-bold">Psychometric Profile</div>
                  <div className="text-sm text-muted-foreground">UPPS-P & Risk Assessment</div>
                </div>
              </div>

              {psychProfile ? (
                <div className="space-y-6">
                  {/* UPPS-P Facets */}
                  <div>
                    <h5 className="font-medium mb-3">UPPS-P Impulsivity Facets</h5>
                    <div className="space-y-3">
                      {[
                        { key: "lack_of_perseverance", label: "Lack of Perseverance", critical: true },
                        { key: "lack_of_premeditation", label: "Lack of Premeditation" },
                        { key: "sensation_seeking", label: "Sensation Seeking" },
                        { key: "positive_urgency", label: "Positive Urgency" },
                        { key: "negative_urgency", label: "Negative Urgency" }
                      ].map(facet => {
                        const value = psychProfile.upps_p?.[facet.key] || 0;
                        return (
                          <div key={facet.key}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className={facet.critical ? "font-medium text-destructive" : ""}>
                                {facet.label} {facet.critical && "(Key Predictor)"}
                              </span>
                              <span>{value}%</span>
                            </div>
                            <Progress value={value} className={facet.critical ? "[&>div]:bg-destructive" : ""} />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Other Scores */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">DOSPERT Health/Safety</div>
                      <div className="text-2xl font-bold">{psychProfile.dospert?.health_safety || 0}%</div>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <div className="text-sm text-muted-foreground">Delay Discounting</div>
                      <div className="text-2xl font-bold">{psychProfile.delay_discounting || 0}%</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Psychometric assessment not completed</p>
                  <p className="text-sm">Worker needs to complete onboarding</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Network Connections */}
          <TabsContent value="connections">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Users className="h-8 w-8 text-primary" />
                <div>
                  <div className="text-lg font-bold">Network Connections</div>
                  <div className="text-sm text-muted-foreground">{connections.length} connections</div>
                </div>
              </div>

              {connections.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No connections recorded</p>
                  <p className="text-sm">Connections are created from check-ins and interactions</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(
                    connections.reduce((acc, c) => {
                      acc[c.connection_type] = acc[c.connection_type] || [];
                      acc[c.connection_type].push(c);
                      return acc;
                    }, {} as Record<string, NetworkConnection[]>)
                  ).map(([type, conns]) => (
                    <div key={type} className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{type.replace(/_/g, " ")}</Badge>
                        <span className="text-sm text-muted-foreground">{conns.length}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {conns.slice(0, 10).map(c => (
                          <span key={c.id} className="text-xs px-2 py-1 bg-background rounded">
                            {c.other_worker_name}
                          </span>
                        ))}
                        {conns.length > 10 && (
                          <span className="text-xs text-muted-foreground">+{conns.length - 10} more</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default UserDetail;
